import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { RouteComponentProps } from "react-router-dom";
import { styles } from "./style";

type History = RouteComponentProps["history"];
type Location = RouteComponentProps["location"];

interface OwnProps {
	// Block navigation while there are unsaved changes. Provide EITHER a reactive boolean (`when`)
	// or a lazy check (`isDirty`) evaluated at navigation time — pages with imperative/ref-based
	// dirty tracking use `isDirty`; pages with dirty state use `when`.
	when?: boolean;
	isDirty?: () => boolean;
	history: History;
}

type Props = OwnProps;

interface DialogProps extends WithStyles<typeof styles> {
	open: boolean;
	onStay: () => void;
	onLeave: () => void;
	message?: string;
	stayLabel?: string;
	leaveLabel?: string;
}

const DialogComponent: React.FC<DialogProps> = ({
	classes = {},
	open,
	onStay,
	onLeave,
	message = "You have unsaved changes. If you leave this page, your changes will be lost.",
	stayLabel = "Stay on Page",
	leaveLabel = "Leave Without Saving",
}) => (
	<Mui.Dialog open={open} onClose={onStay} maxWidth="xs" classes={{ paper: classes.paper }}>
		<Mui.DialogContent className={classes.body}>
			<Mui.Box className={classes.titleRow}>
				<Mui.Box className={classes.titleLeft}>
					<Icons.AlertTriangle size={24} className={classes.warningIcon} />
					<Mui.Typography className={classes.titleText}>Unsaved changes</Mui.Typography>
				</Mui.Box>
				<Mui.IconButton size="small" className={classes.closeButton} onClick={onStay}>
					<Icons.X size={24} />
				</Mui.IconButton>
			</Mui.Box>
			<Mui.Divider className={classes.divider} />
			<Mui.Typography className={classes.bodyText}>{message}</Mui.Typography>
		</Mui.DialogContent>
		<Mui.DialogActions className={classes.actions} disableSpacing>
			<Mui.Button className={classes.leaveButton} onClick={onLeave}>
				{leaveLabel}
			</Mui.Button>
			<Mui.Button className={classes.stayButton} onClick={onStay}>
				{stayLabel}
			</Mui.Button>
		</Mui.DialogActions>
	</Mui.Dialog>
);

// The design-system unsaved-changes modal on its own, for guarding something other than
// navigation (e.g. closing a form dialog).
export const UnsavedChangesDialog = withStyles(DialogComponent, styles);

// Shared unsaved-changes guard. Replaces react-router's native `Prompt` / string-returning
// history.block (both render the browser's window.confirm alert) with the design-system modal
// (Figma 65-4054 / 3214-6086; MAX-3831, MAX-3759). Blocks in-app navigation and lets the user
// Stay or Leave Without Saving.
const Component: React.FC<Props> = ({ when, isDirty, history }) => {
	const [open, setOpen] = useState(false);
	// the full location, so a query string or router state survives the confirmation
	const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
	// Once the user confirms "Leave", we stop blocking so the queued navigation goes through.
	const confirmedRef = useRef(false);
	// Keep the latest dirty check in a ref so the block callback (registered once) always sees
	// current values without re-registering on every render.
	const dirtyRef = useRef<() => boolean>(() => false);
	dirtyRef.current = () => (isDirty ? isDirty() : !!when);

	useEffect(() => {
		const unblock = history.block((location) => {
			if (confirmedRef.current || !dirtyRef.current()) {
				return undefined;
			}
			setPendingLocation(location);
			setOpen(true);
			return false;
		});
		return unblock;
	}, [history]);

	const handleStay = () => {
		setOpen(false);
		setPendingLocation(null);
	};

	const handleLeave = () => {
		confirmedRef.current = true;
		setOpen(false);
		if (pendingLocation) {
			history.push(pendingLocation);
		}
	};

	return <UnsavedChangesDialog open={open} onStay={handleStay} onLeave={handleLeave} />;
};

export const UnsavedChangesPrompt = Component;
